import { Receipt } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useOutletContext, useSearchParams } from 'react-router'

import { importe, METODOS, miProveedor, useMiProveedor } from '../api/agencia'
import { ErrorDeLaApi } from '../api/nucleo'
import { Aviso, Boton, Esqueleto, EstadoVacio, Pastilla, Tarjeta, Titulo } from '../componentes/ui'
import type { Contexto } from './Modulo'

/**
 * «Pagos» del cliente de una agencia revendedora (CLAUDE.md §36, fase R5): a quién paga su plan, hasta cuándo
 * lo tiene pagado, cómo pagar (el PayPal de la agencia, o Yape, Plin y transferencia, que la agencia apunta) y
 * sus recibos. Wixy no aparece: es la agencia quien cobra.
 */
const fecha = (iso: string) => new Date(iso).toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' })
const mensajeDe = (e: unknown, siNo: string) => (e instanceof ErrorDeLaApi ? e.message : siNo)

const Dato = ({ etiqueta, valor }: { etiqueta: string; valor: null | string | undefined }) =>
  valor ? (
    <div className="flex justify-between gap-3 text-sm">
      <span className="text-tenue">{etiqueta}</span>
      <span className="font-medium tabular-nums select-all">{valor}</span>
    </div>
  ) : null

export const Pagos = () => {
  const { espacio } = useOutletContext<Contexto>()
  const { data, isPending, refetch } = useMiProveedor(espacio)
  const [params, setParams] = useSearchParams()
  const [ocupado, setOcupado] = useState(false)
  const [mensaje, setMensaje] = useState<null | { tono: 'error' | 'info' | 'ok'; texto: string }>(null)
  const confirmado = useRef(false)

  // Al volver de PayPal, se le pregunta cómo quedó la suscripción sin esperar a su aviso.
  useEffect(() => {
    const vuelta = params.get('paypal')
    const id = params.get('subscription_id')
    if (!vuelta || confirmado.current) return
    confirmado.current = true
    const limpiar = () => setParams((p) => { p.delete('paypal'); p.delete('subscription_id'); p.delete('ba_token'); p.delete('token'); return p }, { replace: true })
    if (vuelta === 'cancelado') {
      setMensaje({ tono: 'info', texto: 'No se ha hecho ningún pago.' })
      limpiar()
      return
    }
    if (vuelta === 'suscripcion' && id) {
      void miProveedor(espacio)
        .confirmar(id)
        .then((r) => setMensaje(r.estado === 'ACTIVE' ? { tono: 'ok', texto: 'Listo: tu suscripción está activa. El recibo llega por correo en cuanto PayPal confirme el cobro.' } : { tono: 'info', texto: 'PayPal está terminando de confirmar tu suscripción. En unos minutos aparecerá aquí.' }))
        .catch((e) => setMensaje({ tono: 'error', texto: mensajeDe(e, 'No hemos podido comprobar tu suscripción. Recarga en unos minutos.') }))
        .finally(() => {
          limpiar()
          void refetch()
        })
    }
  }, [espacio, params, refetch, setParams])

  if (isPending) return <Esqueleto />
  if (!data) return <Aviso tono="info">Tu cuenta no paga su plan desde aquí.</Aviso>

  const suscribirse = async () => {
    setOcupado(true)
    setMensaje(null)
    try {
      window.location.assign((await miProveedor(espacio).suscribirse()).aprobar)
    } catch (e) {
      setMensaje({ tono: 'error', texto: mensajeDe(e, 'No hemos podido abrir PayPal. Vuelve a intentarlo.') })
      setOcupado(false)
    }
  }
  const cancelar = async () => {
    if (!window.confirm('¿Cancelar la suscripción? Lo que ya tienes pagado sigue valiendo hasta su fecha.')) return
    setOcupado(true)
    try {
      await miProveedor(espacio).cancelar()
      setMensaje({ tono: 'ok', texto: 'Suscripción cancelada. Lo pagado sigue valiendo hasta su fecha.' })
      void refetch()
    } catch (e) {
      setMensaje({ tono: 'error', texto: mensajeDe(e, 'No se ha podido cancelar. Vuelve a intentarlo.') })
    } finally {
      setOcupado(false)
    }
  }

  const { formas } = data
  const activa = data.suscripcion && (data.suscripcion.estado === 'activa' || data.suscripcion.estado === 'suspendida')
  const estado = {
    'al-dia': <Pastilla clave="publicado">Al día</Pastilla>,
    'por-vencer': <Pastilla clave="construyendo">Vence pronto</Pastilla>,
    'en-gracia': <Pastilla clave="construyendo">Vencido</Pastilla>,
    vencido: <Pastilla clave="fallida">Vencido</Pastilla>,
    'sin-pagos': null,
  }[data.estado]
  return (
    <>
      <Titulo descripcion={`Tu plan lo cobra ${data.agencia.nombre}.`} rotulo="Tu cuenta">
        Pagos
      </Titulo>
      {data.suspendidaPorImpago && <Aviso tono="error">Tu cuenta está en pausa porque no consta el pago. En cuanto se registre, vuelve sola.</Aviso>}
      {mensaje && <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso>}
      <Tarjeta>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-[13px] text-tenue">Tu plan</p>
            <p className="font-display text-lg font-semibold">{data.plan?.nombre ?? 'Sin plan'}</p>
            {data.plan && data.plan.precio > 0 && <p className="text-sm tabular-nums">{importe(Math.round(data.plan.precio * 100), data.plan.moneda)} al mes</p>}
          </div>
          <div className="text-right">
            {estado}
            <p className="mt-1 text-sm">{data.pagadoHasta ? `Pagado hasta el ${fecha(data.pagadoHasta)}` : 'Todavía no hay pagos registrados'}</p>
          </div>
        </div>
      </Tarjeta>

      <h2 className="mt-6 mb-3 font-display text-lg font-semibold">Cómo pagar</h2>
      <div className="grid gap-3 md:grid-cols-2">
        {formas.paypal && (
          <Tarjeta>
            <h3 className="font-semibold">PayPal · cada mes</h3>
            {activa ? (
              <>
                <p className="mt-1 text-sm text-tenue">Tienes la suscripción {data.suscripcion!.estado === 'activa' ? 'activa' : 'en pausa en PayPal'}: se cobra sola cada mes.</p>
                <div className="mt-3">
                  <Boton cargando={ocupado} onClick={() => void cancelar()} pequeno>
                    Cancelar la suscripción
                  </Boton>
                </div>
              </>
            ) : (
              <>
                <p className="mt-1 text-sm text-tenue">Con tarjeta o tu cuenta de PayPal. Se cobra sola cada mes y puedes cancelarla cuando quieras.</p>
                <div className="mt-3">
                  <Boton cargando={ocupado} onClick={() => void suscribirse()} tipo="primario">
                    Pagar con PayPal
                  </Boton>
                </div>
              </>
            )}
          </Tarjeta>
        )}
        {formas.yape && (
          <Tarjeta>
            <h3 className="font-semibold">Yape</h3>
            <div className="mt-2 flex flex-col gap-1">
              <Dato etiqueta="Número" valor={formas.yape.numero} />
              <Dato etiqueta="Titular" valor={formas.yape.titular} />
            </div>
          </Tarjeta>
        )}
        {formas.plin && (
          <Tarjeta>
            <h3 className="font-semibold">Plin</h3>
            <div className="mt-2 flex flex-col gap-1">
              <Dato etiqueta="Número" valor={formas.plin.numero} />
              <Dato etiqueta="Titular" valor={formas.plin.titular} />
            </div>
          </Tarjeta>
        )}
        {formas.transferencia && (
          <Tarjeta>
            <h3 className="font-semibold">Transferencia</h3>
            <div className="mt-2 flex flex-col gap-1">
              <Dato etiqueta="Banco" valor={formas.transferencia.banco} />
              <Dato etiqueta="Titular" valor={formas.transferencia.titular} />
              <Dato etiqueta="Cuenta" valor={formas.transferencia.cuenta} />
              <Dato etiqueta="CCI" valor={formas.transferencia.cci} />
            </div>
          </Tarjeta>
        )}
      </div>
      {formas.instrucciones && <p className="mt-3 text-sm whitespace-pre-line">{formas.instrucciones}</p>}
      {!formas.paypal && !formas.yape && !formas.plin && !formas.transferencia && !formas.instrucciones && <Aviso tono="info">Pregúntale a {data.agencia.nombre} cómo pagar tu plan.</Aviso>}
      {(formas.yape || formas.plin || formas.transferencia) && <p className="mt-3 text-[13px] text-tenue">Si pagas a mano, {data.agencia.nombre} lo registra y te llega el recibo por correo.</p>}

      <h2 className="mt-8 mb-3 font-display text-lg font-semibold">Recibos</h2>
      {data.recibos.length === 0 ? (
        <EstadoVacio descripcion="Cada pago que se registre aparecerá aquí, con su recibo para imprimir." icono={<Receipt />} titulo="Todavía no hay recibos" />
      ) : (
        <Tarjeta sinRelleno>
          <ul className="divide-y divide-linea">
            {data.recibos.map((r) => (
              <li className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm" key={r.id}>
                <span className="min-w-0">
                  <strong>Nº {r.numero}</strong> <span className="text-tenue">· {r.concepto}</span>
                </span>
                <span className="flex items-center gap-3 tabular-nums">
                  {importe(r.importe, r.moneda)} <span className="text-tenue">{METODOS[r.metodo] ?? r.metodo} · {new Date(r.fecha).toLocaleDateString('es')}</span>
                  <a className="text-violeta hover:underline" href={`/api/panel/recibo?id=${r.id}`} rel="noreferrer" target="_blank">
                    Ver
                  </a>
                </span>
              </li>
            ))}
          </ul>
        </Tarjeta>
      )}
    </>
  )
}
